import {
  computed, ref, watch, toRaw,
} from 'vue';
import { fireTag } from '@vue-util/tagManager';
import useFireTagAsync from '@modules/discovery/composables/useFireTagAsync';
import useFacets from './useFacets';
import useMultiSelectDefault from './useMultiSelectDefault';
import useSortFilter from './useSortFilter';
import deepCopyComputedValue from '../utils/deepCopyComputedValue';
import groupByKey from '../utils/groupByKey';
import { isNotDeliveryAndPickupFacet } from '../utils/deliveryAndPickupUtil';

export default function useDeselectionPills(props) {
  const {
    allSelectedFacets,
    globalOptions,
    deselectionPillsNamesOrder,
    setDeselectionPillsOrderNames,
  } = useFacets();

  const {
    selections, rightSideDrawer,
  } = useSortFilter();

  const { isSYS } = globalOptions;
  const enableAsyncAnalyticsTags = globalOptions?.consumerStore?.state?.envProps?.enableAsyncAnalyticsTags;
  const deselectionValues = ref([]);

  // inside a drawer facets are used from the local selections otherwise use a store
  const selectedFacets = props.drawer ? selections.value.facets : allSelectedFacets.value;
  const currentFacet = ref(props.facet);
  let selectedValues;
  if (isSYS.value) {
    selectedValues = ref(deepCopyComputedValue(selectedFacets));
  } else {
    selectedValues = ref(deepCopyComputedValue(selectedFacets).filter(isNotDeliveryAndPickupFacet));
  }
  const noOrderDeselectionValues = computed(() => selectedValues?.value?.reduce((array, facet) => {
    facet?.selectedValues?.forEach((item) => {
      if (facet?.children?.length) {
        item?.selectedValues?.forEach((val) => {
          const value = val.split('_')[0];
          /* istanbul ignore next */
          array.push({
            name: item.name, value, label: value, parent: facet.displayName, origVal: val, selectedValues: [...(item?.selectedValues || [])],
          });
        });
      } else {
        const foundVal = facet?.values?.find((obj) => obj.value === item);
        /* istanbul ignore next */
        array.push({
          name: facet.displayName, label: foundVal?.label, value: foundVal?.value, selectedValues: [...(facet?.selectedValues || [])],
        });
      }
    });

    return array;
  }, []));
  /**
   * @param {Array} pills
   * @returns {Array<string>}
   */
  /* istanbul ignore next */
  const getPillsName = (pills) => pills?.map((pill) => pill.name) || [];

  /**
   * @param {Array} newPills
   * @param {Array} currentPills
   * @returns {Array}
   */
  const moveNamesToTheEnd = (newPills, currentPills = []) => {
    const newNamesGroup = groupByKey(newPills, 'name');
    const existingNamesGroup = groupByKey(currentPills, 'name');

    let facetName;
    Object.keys(newNamesGroup).forEach((name) => {
      /* istanbul ignore next */
      if (newNamesGroup[name]?.length > existingNamesGroup[name]?.length) {
        facetName = name;
      }
    });

    const currentNamesOrder = getPillsName(currentPills);
    if (facetName) {
      const _existingNames = currentNamesOrder.filter((name) => name !== facetName);
      return [..._existingNames, facetName];
    }

    return currentNamesOrder;
  };

  /**
   * @param {Array} newPills
   * @param {Array} previousPills
   * @param {Array<string>} storeNamesOrder
   * @returns {Array<string>}
   * use local deselectionValues or use store data to sort new pills
   */
  const getActualNamesOrder = (newPills, previousPills, storeNamesOrder) => {
    let namesOrder = [];
    if (previousPills?.length) {
      namesOrder = getPillsName(previousPills);
    } else {
      namesOrder = storeNamesOrder.length ? storeNamesOrder : getPillsName(newPills);
    }

    return namesOrder;
  };

  /**
   * @param {Array<string>} newNames
   * @param {Array<string>} previuosNames
   * @returns
   */
  // eslint-disable-next-line arrow-body-style
  const doSameFacetAdded = (newNames, previuosNames) => {
    return newNames?.length > previuosNames?.length && ([...new Set(newNames)].length === [...new Set(previuosNames)].length);
  };

  const sortPillsbyName = (sortedPills, namesOrder = []) => {
    sortedPills.sort((a, b) => {
      const aIndex = namesOrder.indexOf(a.name);
      const bIndex = namesOrder.indexOf(b.name);
      let order = -1;
      /* istanbul ignore else */
      if (aIndex > -1 && bIndex > -1) {
        order = aIndex - bIndex;
      }
      /* istanbul ignore else */
      if (aIndex === -1) {
        order = 1;
      }

      return order;
    });
  };

  /**
   * @param {Array} pills
   * @param {Array} previousPills
   * @returns Array
   * pills aren't being returned by xapi don't have label & value
   */
  const fillEmptyPills = (pills, previousPills) => {
    const pillsgrouped = groupByKey(pills, 'name');
    const filledPills = Object.values(pillsgrouped)
      .map((group) => group.map((pill, index) => {
        /* istanbul ignore next */
        if (!pill.value) {
          // selectedValues stores all selected values even when an option isn't being returned by xapi
          const value = pill.selectedValues[index];
          const { label } = previousPills.find((p) => p.name === pill.name && p.value === value) || {};

          return {
            ...pill,
            value,
            label,
          };
        }
        return pill;
      }))
      .flatMap((groups) => groups);

    return filledPills;
  };

  watch(noOrderDeselectionValues, (newPills) => {
    const newNames = getPillsName(newPills);
    const previuosNames = getPillsName(deselectionValues.value);
    const storeNamesOrder = toRaw(deselectionPillsNamesOrder.value);
    /* istanbul ignore next */
    let currentNamesOrder = newPills?.length ? getActualNamesOrder(newPills, deselectionValues.value, storeNamesOrder) : [];

    const isSameFacetAdded = doSameFacetAdded(newNames, previuosNames);

    // we have to move all facet bucket to the end
    /* istanbul ignore else */
    if (isSameFacetAdded) {
      currentNamesOrder = moveNamesToTheEnd(newPills, deselectionValues.value);
    }

    /* istanbul ignore next */
    const sortedPills = newPills?.slice() || [];
    sortPillsbyName(sortedPills, currentNamesOrder);

    // options aren't being returned by xapi
    const emptyPills = sortedPills.filter((pill) => !pill.value);

    let pills = sortedPills;
    /* istanbul ignore next */
    if (emptyPills.length) {
      pills = fillEmptyPills(pills, deselectionValues.value);
    }
    setDeselectionPillsOrderNames(currentNamesOrder);

    deselectionValues.value = pills;
    /* istanbul ignore else */
    if (!rightSideDrawer.value && currentFacet?.value) {
      deselectionValues.value = deselectionValues.value.filter((el) => el.name === currentFacet.value.displayName
      || el.parent === currentFacet.value.displayName);
    }
    /* istanbul ignore else */
    if (isSYS.value) {
      deselectionValues.value = deselectionValues.value.filter((selectionValue) => selectionValue.name !== 'DELIVERY_AND_PICKUP');
    }
  }, { immediate: true });

  const setSelectedValues = (newSelectedValues) => {
    if (isSYS.value) {
      selectedValues.value = newSelectedValues;
    } else {
      selectedValues.value = newSelectedValues.filter(isNotDeliveryAndPickupFacet);
    }
  };

  watch(() => allSelectedFacets.value, (newVal) => {
    /* istanbul ignore else */
    if (!props.drawer) {
      setSelectedValues(newVal);
    }
  });

  watch(() => selections.value.facets, (newVal) => {
    /* istanbul ignore else */
    if (props.drawer) {
      setSelectedValues(newVal);
    }
  });

  const onDeselect = (obj) => {
    const filteredFacet = selectedValues?.value?.find((facet) => facet.displayName === obj.name || facet.displayName === obj.parent);

    if (filteredFacet?.children?.length) {
      let index = 0;
      const filteredChild = filteredFacet?.selectedValues?.find((item, idx) => {
        index = idx;
        return item.name === obj.name;
      });

      const updatedValues = filteredChild?.selectedValues?.filter((val) => val !== obj.origVal);
      if (updatedValues.length) {
        filteredChild.selectedValues = updatedValues;
      } else {
        filteredFacet?.selectedValues?.splice(index, 1);
      }
    } else {
      const updatedValues = filteredFacet?.selectedValues?.filter((val) => val !== obj.value);
      filteredFacet.selectedValues = updatedValues;
    }

    if (props.drawer) {
      const result = selectedValues?.value?.filter((item) => item.selectedValues.length);
      globalOptions.onSelectedOption(result);
    } else {
      filteredFacet.alwaysDropdown = true;
      const { updateCheckboxCriteria } = useMultiSelectDefault({ facet: filteredFacet });
      updateCheckboxCriteria();
    }

    if (enableAsyncAnalyticsTags) {
      useFireTagAsync(() => fireTag('link', {
        event_name: 'plp facet pill remove',
        link_name: 'plp facet pill remove',
      }));
    } else {
      fireTag('link', {
        event_name: 'plp facet pill remove',
        link_name: 'plp facet pill remove',
      });
    }
  };

  return {
    deselectionValues,
    onDeselect,
    getActualNamesOrder,
    moveNamesToTheEnd,
    fillEmptyPills,
    doSameFacetAdded,
    sortPillsbyName,
  };
}
